import type { AuthMode, RoleToken } from '../auth/types';

/** CustomEvent name fired whenever the runtime-config singleton
 *  changes (file poll, programmatic override, etc.). Subscribers can
 *  either listen via `window.addEventListener` or via the typed
 *  `onRuntimeConfigChange` helper in `runtime-config-reload.ts`. */
export const RUNTIME_CONFIG_UPDATED_EVENT = 'nexsoss:runtime-config-updated';

export interface RuntimeZitadelConfig {
  issuer: string;
  clientId: string;
  scopes: string[];
  redirectUri: string;
  postLogoutRedirectUri: string;
  audience?: string;
}

export interface RuntimeSite {
  id: string;
  label: string;
}

export interface RuntimeTaskType {
  id: string;
  label: string;
  category: 'foh' | 'boh' | 'service';
  description?: string;
}

export interface RuntimeFeatureFlags {
  /**
   * The 3D building view at `/twin`.
   *
   * Optional and defaulting to OFF, which is the shape `certInfoEnabled`
   * established: a deployed runtime-config that predates this release must
   * not fail validation, and a site that has not opted in must not get the
   * route.
   *
   * NOTE, because the obvious claim about it is false: this does NOT hot-flip
   * a mounted route. `runtime-config-reload.ts` re-polls every five minutes
   * and notifies listeners, but nothing in `App.tsx` subscribes, so the route
   * table is fixed for the life of the page. Turning the flag off removes the
   * route on the next load, not the next poll. Do not describe it as a kill
   * switch.
   */
  twinEnabled?: boolean;
  /**
   * The attention list and verdict bar on the Overview, for an OPS
   * audience: any of `platform_admin`, `support_l2`, `support_l3`
   * (`audienceFor`, twin slice 1 contract §8).
   *
   * Optional and defaulting to OFF, as `twinEnabled`. Read whenever the
   * Overview renders the list (`useAttentionModel`), so a re-polled config
   * takes effect on the next render, not by itself: not a kill switch.
   */
  attentionEnabled?: boolean;
  /**
   * The same list for a SITE audience: `foh_operator`, `site_supervisor`
   * and `service_operator` (contract §12 A2), and a login with no known
   * role. Optional and defaulting to OFF; it stays off until the iPad
   * go-live gate (the written e-stop procedure, the support number on
   * screen, the real-iPad check).
   */
  attentionForSiteStaff?: boolean;
  alertAckEnabled: boolean;
  alertResolveEnabled: boolean;
  taskDispatchEnabled: boolean;
  robotRecallEnabled: boolean;
  auditExportEnabled: boolean;
  /**
   * DEAD — deprecated, optional, tolerated if present.
   *
   * This flag only ever gated the visibility of the Devices screen's
   * "Certificate info not yet wired" placeholder banner, which was
   * removed (there is no cert metadata anywhere server-side to show;
   * see the 2026-09-02 UI batch). Nothing reads it any more. It stays
   * in the type this release ONLY so a strict reading of the deployed
   * runtime-config JSONs — which still carry the key — cannot fail in
   * either direction (key present with an old bundle, key absent with
   * a new one). Delete it from the deployed JSONs, and then from this
   * type, next release.
   */
  certInfoEnabled?: boolean;
  userListEnabled: boolean;
  /**
   * When true, SPA write paths (`POST /tasks`, `POST /alerts/:id`,
   * and the `useCreateTask` / `useUpdateAlert` mutations) include
   * the active site id in the request body so the backend can scope
   * the entity to the user's site. Disable in production until the
   * real RMF API is updated to accept the field; the SPA will fall
   * back to the pre-fix global behaviour. See ADR 0001 § Realtime
   * Transport & Reliability.
   */
  siteIdInWriteBodies: boolean;
  /**
   * When true, screens filter robots/tasks/alerts strictly by the
   * active site (entity.siteId === site.id) — entities without a
   * siteId are hidden. The mock server tags every entity, so this
   * is safe for mock/local/dev. In production, the real RMF API
   * does not yet tag every entity, so the strict filter would hide
   * valid data; the default is the loose filter
   * `!entity.siteId || entity.siteId === site.id` which treats
   * untagged entities as global. Flip on in production once the
   * real API tags every entity.
   */
  siteFilterStrict: boolean;
}

export interface RuntimeConfig {
  authMode: AuthMode;
  env: 'local' | 'staging' | 'production';
  zitadel: RuntimeZitadelConfig;
  /**
   * HTTP(S) base URL for the global API and fetch-SSE live path.
   * In the convergence target (plan §6.4) this points at Envoy
   * (`https://localhost` in compose, or the prod Gateway URL in
   * K8s). Both REST (`/api/v1/**`) and fetch-SSE (`/live/v1/**`)
   * share the same origin — there is no separate `wsBaseUrl`.
   */
  apiBaseUrl: string;
  /** The number the report form offers for something urgent. Absent: no line
   *  at all, rather than a placeholder in front of a hotel. */
  supportPhone?: string;
  client: {
    id: string;
    label: string;
    organisation: string;
  };
  featureFlags: RuntimeFeatureFlags;
  defaultRoleForMock?: RoleToken;
}

let cached: RuntimeConfig | null = null;

const RUNTIME_CONFIG_URL = '/config/runtime-config.json';

export async function loadRuntimeConfig(fetchImpl: typeof fetch = fetch): Promise<RuntimeConfig> {
  const res = await fetchImpl(RUNTIME_CONFIG_URL, { cache: 'no-store' });
  if (!res.ok) {
    throw new Error(`Failed to load runtime-config.json: HTTP ${res.status}`);
  }
  const raw = (await res.json()) as RuntimeConfig;
  validateRuntimeConfig(raw);
  cached = raw;
  return raw;
}

export function setRuntimeConfig(cfg: RuntimeConfig): void {
  cached = cfg;
}

/**
 * Patch the live site list on the cached runtime-config singleton.
 *
 * REMOVED in the convergence target (plan §6.4: "Keep runtime config
 * limited to edge/API bootstrap and OIDC values; remove static
 * security-authoritative sites, wsBaseUrl, seed registries/catalogs,
 * and loose site filters."). Sites are sourced exclusively from the
 * global `nexsoss-api-server` `/api/v1/sites` endpoint and live in
 * SiteContext, not in runtime-config.
 */

export function getRuntimeConfig(): RuntimeConfig {
  if (!cached) {
    throw new Error('Runtime config not loaded. Call loadRuntimeConfig() during bootstrap.');
  }
  return cached;
}

export function getOptionalRuntimeConfig(): RuntimeConfig | null {
  return cached;
}

function isNonEmptyString(v: unknown): v is string {
  return typeof v === 'string' && v.length > 0;
}

// P0 §1.2 — TLS enforcement. Production endpoints must use the
  // TLS-only schemes; staging enforces TLS for cross-host endpoints
  // but allows http://localhost for in-cluster dev wiring.
export function validateRuntimeConfig(cfg: RuntimeConfig): void {
  const errors: string[] = [];
  if (!cfg || typeof cfg !== 'object') {
    throw new Error('Invalid runtime-config.json:\n - config root must be an object');
  }
  if (cfg.authMode !== 'zitadel' && cfg.authMode !== 'mock') {
    errors.push(`authMode must be 'zitadel' or 'mock' (got '${cfg.authMode}')`);
  }
  if (cfg.authMode === 'mock') {
    // Mock auth is a dev/CI convenience. We refuse it on every
    // non-local environment — even staging — so a leaked mock
    // config cannot accidentally ship to a real tenant cluster.
    // The full list of environments is `local | staging | production`;
    // any value outside that set is treated as production-like.
    if (cfg.env !== 'local') {
      errors.push(`authMode=mock is not permitted when env='${cfg.env}' (only env='local')`);
    }
  }
  if (!isNonEmptyString(cfg.apiBaseUrl)) errors.push('apiBaseUrl must be a non-empty string');
  if (!isNonEmptyString(cfg.client?.id)) errors.push('client.id must be a non-empty string');
  // Sites and defaultSiteId are REMOVED from runtime-config in the
  // convergence target (plan §6.4). The SPA sources sites from
  // `nexsoss-api-server`'s `/api/v1/sites` endpoint and active site
  // selection lives in SiteContext.
  if (cfg.authMode === 'zitadel') {
    const z = cfg.zitadel;
    if (!isNonEmptyString(z.issuer)) errors.push('zitadel.issuer required when authMode=zitadel');
    if (!isNonEmptyString(z.clientId)) errors.push('zitadel.clientId required when authMode=zitadel');
    if (!Array.isArray(z.scopes) || z.scopes.length === 0) {
      errors.push('zitadel.scopes must be a non-empty array');
    }
    if (!isNonEmptyString(z.redirectUri)) errors.push('zitadel.redirectUri required');
    if (!isNonEmptyString(z.postLogoutRedirectUri)) {
      errors.push('zitadel.postLogoutRedirectUri required');
    }
  }
  // P0 §1.2 — TLS enforcement. Production endpoints must use the
  // TLS-only schemes; staging enforces TLS for cross-host endpoints
  // but allows http://localhost for in-cluster dev wiring.
  if (cfg.env === 'production') {
    if (!cfg.apiBaseUrl.startsWith('https://')) {
      errors.push('apiBaseUrl must use https:// in production');
    }
  }
  if (errors.length > 0) {
    throw new Error(`Invalid runtime-config.json:\n - ${errors.join('\n - ')}`);
  }
}